import { Panel } from "./panel-base.ts";
import { el, formatRelativeVi } from "../utils/dom.ts";
import { getNews, type NewsItem } from "../services/api-client.ts";
import { appContext } from "../app/app-context.ts";

export interface TopicFilter {
  key: string;
  label: string;
}

const ECONOMY_TOPIC_FILTERS: TopicFilter[] = [
  { key: "all", label: "Tất cả" },
  { key: "gold", label: "Vàng" },
  { key: "oil", label: "Dầu" },
  { key: "fx", label: "Tỷ giá" },
  { key: "macro", label: "Vĩ mô" },
];

export interface NewsFeedOptions {
  id: string;
  title: string;
  category?: string; // server-side axis filter (economy|policy|social); undefined = all
  topicFilters?: TopicFilter[]; // client-side sub-filter chips; omit for no chips
  initialTopic?: string; // client-side topic to pre-select (default "all")
  useProvinceFilter?: boolean; // subscribe to province selection (economy/map tab only)
}

export class NewsFeedPanel extends Panel {
  private items: NewsItem[] = [];
  private topicFilter: string;
  private readonly opts: NewsFeedOptions;
  private readonly onProvinceChange?: () => void;

  constructor(opts: NewsFeedOptions) {
    super({ id: opts.id, title: opts.title });
    this.opts = opts;
    this.topicFilter = opts.initialTopic ?? "all";
    this.setLoading();
    // Only the province-aware feed subscribes; store the handler so dispose() can detach it
    // (3 tabs mounting 3 feeds must not accumulate live listeners — red-team #12).
    if (opts.useProvinceFilter) {
      this.onProvinceChange = () => this.render();
      appContext.on("province-change", this.onProvinceChange);
    }
  }

  dispose(): void {
    if (this.onProvinceChange) appContext.off("province-change", this.onProvinceChange);
  }

  async load(): Promise<void> {
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (this.opts.category) params.set("category", this.opts.category);
      if (this.opts.useProvinceFilter && appContext.selectedProvince) {
        params.set("province", appContext.selectedProvince);
      }
      const qs = params.toString();
      const { items } = await getNews(qs ? `?${qs}` : "");
      this.items = items;
      this.render();
    } catch {
      this.setError();
    }
  }

  private matchesTopic(item: NewsItem): boolean {
    if (this.topicFilter === "all") return true;
    if (!item.topics) return false;
    try {
      return (JSON.parse(item.topics) as string[]).includes(this.topicFilter);
    } catch {
      return false;
    }
  }

  private render(): void {
    const nodes: (Node | string)[] = [];

    const filters = this.opts.topicFilters ?? [];
    if (filters.length > 0) {
      nodes.push(
        el(
          "div",
          { className: "news-filters" },
          filters.map((f) =>
            el(
              "button",
              {
                className: f.key === this.topicFilter ? "news-filter active" : "news-filter",
                onclick: () => {
                  this.topicFilter = f.key;
                  this.render();
                },
              },
              [f.label],
            ),
          ),
        ),
      );
    }

    if (this.opts.useProvinceFilter && appContext.selectedProvince) {
      nodes.push(
        el("div", { className: "news-province-note" }, [
          "Lọc theo tỉnh đã chọn ",
          el("button", { className: "clear-filter", onclick: () => appContext.setProvince(null) }, [
            "✕ bỏ lọc",
          ]),
        ]),
      );
    }

    const filtered = this.items.filter((i) => this.matchesTopic(i));
    nodes.push(
      filtered.length === 0
        ? el("div", { className: "panel-empty" }, ["Chưa có tin phù hợp."])
        : el(
            "ul",
            { className: "news-list" },
            filtered.map((item) =>
              el("li", { className: "news-item" }, [
                el("a", { href: item.url, target: "_blank", rel: "noopener noreferrer" }, [
                  item.title,
                ]),
                el("div", { className: "news-meta" }, [
                  el("span", { className: "news-source" }, [item.source ?? ""]),
                  el("span", { className: "news-time" }, [
                    item.published_at ? formatRelativeVi(item.published_at) : "",
                  ]),
                ]),
              ]),
            ),
          ),
    );

    this.setContent(...nodes);
  }
}

export { ECONOMY_TOPIC_FILTERS };
